import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Calendar } from '../../components/ui/calendar';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { getAllBookings, updateAvailability, getCustomAvailability } from '../../utils/adminApi';

const AdminCalendar = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [dayBookings, setDayBookings] = useState([]);
  const [timeSlots, setTimeSlots] = useState([
    { time: '8:00 AM', available: true },
    { time: '9:00 AM', available: true },
    { time: '10:00 AM', available: true },
    { time: '11:00 AM', available: true },
    { time: '12:00 PM', available: true },
    { time: '1:00 PM', available: true },
    { time: '2:00 PM', available: true },
    { time: '3:00 PM', available: true },
    { time: '4:00 PM', available: true }
  ]);

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const dayBookings = bookings.filter(b => b.date === dateStr);
      setDayBookings(dayBookings);
      
      // Update time slots based on bookings
      const bookedTimes = dayBookings.map(b => b.time);
      setTimeSlots(timeSlots.map(slot => ({
        ...slot,
        available: !bookedTimes.includes(slot.time)
      })));
    }
  }, [selectedDate, bookings]);

  const fetchBookings = async () => {
    try {
      const data = await getAllBookings();
      setBookings(data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const toggleSlotAvailability = (time) => {
    setTimeSlots(timeSlots.map(slot =>
      slot.time === time ? { ...slot, available: !slot.available } : slot
    ));
  };

  const saveAvailability = async () => {
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      await updateAvailability(dateStr, timeSlots);
      toast.success('Availability updated');
    } catch (error) {
      toast.error('Failed to update availability');
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-zinc-100">Calendar & Availability</h1>
        <p className="text-zinc-400 mt-1">Manage bookings and availability</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Calendar */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-zinc-100">Select Date</CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              className="rounded-md border border-zinc-800 bg-zinc-950"
              classNames={{
                months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                month: "space-y-4",
                caption: "flex justify-center pt-1 relative items-center text-zinc-100",
                caption_label: "text-sm font-medium",
                nav: "space-x-1 flex items-center",
                nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-zinc-400",
                nav_button_previous: "absolute left-1",
                nav_button_next: "absolute right-1",
                table: "w-full border-collapse space-y-1",
                head_row: "flex",
                head_cell: "text-zinc-500 rounded-md w-9 font-normal text-[0.8rem]",
                row: "flex w-full mt-2",
                cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-zinc-800 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-zinc-800 rounded-md text-zinc-300",
                day_selected: "bg-zinc-100 text-zinc-950 hover:bg-zinc-200 hover:text-zinc-950 focus:bg-zinc-100 focus:text-zinc-950",
                day_today: "bg-zinc-800 text-zinc-100",
                day_outside: "text-zinc-600 opacity-50",
                day_disabled: "text-zinc-600 opacity-50",
                day_hidden: "invisible",
              }}
            />
          </CardContent>
        </Card>

        {/* Day Schedule */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-zinc-100">
              {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-zinc-400 mb-3">Bookings ({dayBookings.length})</h3>
                {dayBookings.length === 0 ? (
                  <p className="text-zinc-500 text-sm">No bookings for this day</p>
                ) : (
                  <div className="space-y-2">
                    {dayBookings.map(booking => (
                      <div key={booking.bookingId} className="p-3 bg-zinc-950 rounded border border-zinc-800">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-semibold text-zinc-100">{booking.time}</div>
                            <div className="text-sm text-zinc-400">{booking.customerName}</div>
                            <div className="text-xs text-zinc-500">{booking.serviceName}</div>
                          </div>
                          <div className={`text-xs px-2 py-1 rounded ${
                            booking.status === 'confirmed' ? 'bg-green-500/10 text-green-500' :
                            'bg-yellow-500/10 text-yellow-500'
                          }`}>
                            {booking.status}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold text-zinc-400 mb-3">Time Slot Availability</h3>
                <div className="grid grid-cols-3 gap-2">
                  {timeSlots.map(slot => (
                    <Button
                      key={slot.time}
                      size="sm"
                      variant={slot.available ? 'outline' : 'secondary'}
                      onClick={() => toggleSlotAvailability(slot.time)}
                      className={`${
                        slot.available 
                          ? 'bg-zinc-950 text-zinc-300 border-zinc-800 hover:bg-zinc-800'
                          : 'bg-red-500/20 text-red-400 border-red-800'
                      }`}
                    >
                      {slot.time}
                    </Button>
                  ))}
                </div>
                <Button
                  onClick={saveAvailability}
                  className="w-full mt-4 bg-zinc-100 text-zinc-950 hover:bg-zinc-200"
                >
                  Save Availability
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminCalendar;
